/* ===========================================================
   Lucraa — Dados mockados da loja demo "Koloni Skate Shop"
   (fonte da verdade — mês corrente). Catálogo, preços e identidade
   vêm da loja REAL koloniskateshop.com (Nuvemshop, Santa Isabel/SP);
   os números financeiros são SINTÉTICOS — coerentes entre si, mas
   não são os dados reais da loja. Nada pode se contradizer entre
   telas — edite só aqui. Servidos pelo src/api/client.ts, chaveados
   por storeId em src/mocks/tenants.ts.
   =========================================================== */
import type { PedidoAuditavel, StoreData, WaterfallStep } from "../api/types";
import { faixaParaFaturamento } from "../domain/faixa";
import { resolverAtribuicao, type SinaisAtribuicao } from "../domain/atribuicao";
import { breakevenRoas } from "../domain/roas";
import stkShape from "../assets/shape-skate-fake.svg";
import stkTenis from "../assets/tenis-skate-fake.svg";
import stkCalca from "../assets/calca-cargo-fake.svg";

const nuvemshop = {
  loja: "Koloni Skate Shop",
  segmento: "Skate e streetwear",
  periodo: "mês corrente",
  // coerência: pedidos ≈ sessões × conversão (20.000 × 1,2%) e
  // ticketMedio = receita ÷ pedidos — ver koloni-skateshop.test.ts
  receita: 50000,
  descontos: 3000, // cupons do período; receita (líquida) + descontos = bruta (53.000)
  pedidos: 240,
  ticketMedio: 208.33,
  conversao: 0.012,
  produtos: [
    {
      nome: "Tênis Tesla Flow Black/White",
      vendas: 46,
      margem: 0.42,
      be: 2.4,
      gastoAds: 2800,
      fonteGastoAds: "catalogo" as const,
      roasReal: 5.1,
      roasPlat: 6.8,
      cpa: 61,
      conv: 0.028,
      estoqueDias: 4,
      faturamento: 14260,
      visualizacoes: 1640,
    },
    {
      nome: "Camiseta Huf Ice Dice",
      vendas: 165,
      margem: 0.11,
      be: 9.1,
      gastoAds: 3900,
      fonteGastoAds: "destino_anuncio" as const,
      roasReal: 3.6,
      roasPlat: 5.2,
      cpa: 24,
      conv: 0.044,
      estoqueDias: 34,
      faturamento: 13200,
      visualizacoes: 3750,
    },
    {
      nome: "Shape Maple Koloni Santa",
      vendas: 34,
      margem: 0.46,
      be: 2.2,
      gastoAds: 2100,
      fonteGastoAds: "catalogo" as const,
      roasReal: 4.6,
      roasPlat: 5.9,
      cpa: 62,
      conv: 0.024,
      estoqueDias: 6,
      faturamento: 9520,
      visualizacoes: 1420,
    },
    {
      nome: "Skate Montado Hondar Iniciante Gold Rush",
      vendas: 18,
      margem: 0.49,
      be: 2.0,
      gastoAds: 1600,
      fonteGastoAds: "destino_anuncio" as const,
      roasReal: 2.6,
      roasPlat: 4.4,
      cpa: 89,
      conv: 0.009,
      estoqueDias: 45,
      faturamento: 7200,
      visualizacoes: 2000,
    },
  ],
  carrinhosAbandonados: { valorRecuperavel: 4200, qtd: 20 },
  clientesInativos90d: { qtd: 170, potencialRecuperacao: 28000 },
  // coerência: receita − ads − cmv − frete − imposto − embalagem = lucroLiquido (11.200)
  // Embalagem (R$ 1.560 = 240 pedidos × R$ 6,5, o embalagemDefault da loja) é uma
  // linha própria da cascata na v1 — destacada de "Frete & Logística" (4.200 → 2.640),
  // sem mexer no total de custos variáveis (27.800), na margem de contribuição (22.200)
  // nem no lucro (11.200). Simplificação de demo (NÃO vira regra: em produção embalagem
  // é custo próprio, somado ao frete, não subtraído) — database-schema.md §6, decisions.md
  // §24, docs/calculo-composicao-lucro.md §4b.
  custos: { cmv: 21000, frete: 2640, imposto: 2600, embalagem: 1560 },
};

const analytics = {
  sessoes: 20000,
  conversao: 0.012,
  conversaoSegmento: 0.019,
  conversaoTop10: 0.031,
  canais: [
    { canal: "Tráfego pago", sessoes: 8400, receita: 26000 },
    { canal: "Orgânico", sessoes: 5600, receita: 11000 },
    { canal: "Direto", sessoes: 3600, receita: 8000 },
    { canal: "Social", sessoes: 2400, receita: 5000 },
  ],
  deltaConversao: -0.12,
  deltaFreteMedio: 0.09,
};

// Degradê automático (decisions.md §18): toda campanha listada foi mapeada sem
// intervenção humana (vinculo = degrau do join); o que não casa NÃO vira fila —
// agrega no canal como gastoSemCampanha e, no nível de produto, soma em
// gastoNaoAtribuivelProduto. Invariantes em koloni-skateshop.test.ts.
const ads = {
  investimentoTotal: 11000,
  investimentoMeta: 8000,
  investimentoGoogle: 3000,
  campanhas: [
    {
      externalId: "23851840921660123",
      provider: "meta_ads" as const,
      plataforma: "Meta",
      nome: "Drop de Inverno",
      investido: 4800,
      roasInformado: 4.2,
      roasReal: 2.9,
      lucroLiquido: -1900,
      vinculo: "utm_id" as const,
    },
    {
      externalId: "23851877410230123",
      provider: "meta_ads" as const,
      plataforma: "Meta",
      nome: "Retargeting",
      investido: 2800,
      roasInformado: 6.1,
      roasReal: 5.2,
      lucroLiquido: 3100,
      vinculo: "auto_append" as const,
    },
    {
      externalId: "21487326544",
      provider: "google_ads" as const,
      plataforma: "Google",
      nome: "Search Marca",
      investido: 1200,
      roasInformado: 9.0,
      roasReal: 8.4,
      lucroLiquido: 2600,
      vinculo: "gclid" as const,
    },
    {
      externalId: "21490112837",
      provider: "google_ads" as const,
      plataforma: "Google",
      nome: "Shopping Geral",
      investido: 1800,
      roasInformado: 3.1,
      roasReal: 2.0,
      lucroLiquido: -200,
      vinculo: "gclid" as const,
    },
  ],
  // 400 do canal Meta sem campanha identificada (cliques antigos sem utm_id) +
  // 200 de campanha genérica que não desce a produto → 600 fora do nível produto.
  gastoSemCampanha: 400,
  gastoNaoAtribuivelProduto: 600,
};

const derivados = {
  lucroLiquido: 11200,
  margemLiquida: 0.224,
  roasMedio: 4.2,
  roasReal: 2.9,
  // breakeven blended (domain): 50.000 ÷ (50.000 − 21.000 − 2.640 − 2.600 − 1.560) ≈ 2,25x
  roasEquilibrio: breakevenRoas(
    nuvemshop.receita,
    nuvemshop.custos.cmv +
      nuvemshop.custos.frete +
      nuvemshop.custos.imposto +
      nuvemshop.custos.embalagem,
  ),
  deltaLucro: -0.14,
  deltaReceita: -0.18,
  deltaConversao: -0.12,
  // coerência: soma dos `potencial` dos monitores (680 + 4110 + 2100)
  recuperavelMes: 6890,
  respostaDoDia:
    "Reponha o Shape Maple Koloni Santa (zera em 6 dias) e pause a campanha Meta “Drop de Inverno” — ela está dando prejuízo de R$ 1.900.",
  lucroPeriodos: {
    hoje: {
      label: "Hoje",
      receita: 1660,
      lucro: 373,
      anterior: { label: "ontem", receita: 1780, lucro: 395 },
    },
    "7d": {
      label: "Últimos 7 dias",
      receita: 13100,
      lucro: 2890,
      anterior: { label: "7 dias anteriores", receita: 12600, lucro: 2740 },
    },
    "30d": {
      label: "Últimos 30 dias",
      receita: 58200,
      lucro: 12640,
      anterior: { label: "30 dias anteriores", receita: 54900, lucro: 11800 },
    },
    "este-mes": {
      label: "Este mês",
      receita: 50000,
      lucro: 11200,
      anterior: { label: "mês passado", receita: 61000, lucro: 13000 },
    },
    "mes-passado": {
      label: "Mês passado",
      receita: 61000,
      lucro: 13000,
      anterior: { label: "dois meses atrás", receita: 58200, lucro: 12100 },
    },
    "90d": {
      label: "Últimos 90d",
      receita: 169200,
      lucro: 36300,
      anterior: { label: "90 dias anteriores", receita: 158400, lucro: 33100 },
    },
  },
  monitores: [
    {
      id: "vendas",
      tag: "Vendas",
      icone: "cart",
      titulo: "9 carrinhos abandonados nas últimas 48h",
      resumo:
        "Clientes que abandonaram o checkout nas últimas 48 horas. Recuperação manual via cupom converte 15–25%.",
      valorRisco: 2740,
      valorLabel: "parados no checkout",
      insight:
        "Envie um cupom de 10% para os 9 clientes — recuperando 15–25%, você traz de volta R$ 410 a R$ 680 ainda hoje.",
      cta: "Criar campanha",
      tipo: "campanha",
      potencial: 680,
      detalheCta: "Ver detalhes",
      detalheTitulo: "Carrinhos abandonados · últimas 48h",
      adminUrl: "https://koloniskateshop.lojavirtualnuvem.com.br/admin/abandoned-carts/",
      carrinhos: [
        {
          id: 1991207334,
          nome: "Mariana Souza Lima",
          email: "mariana.slima@gmail.com",
          fone: "+5511998124471",
          itens: 2,
          valor: 399.98,
          quando: "há 3h",
        },
        {
          id: 1991201882,
          nome: "Camila Rocha",
          email: "camis.rocha@hotmail.com",
          fone: "+5511988771209",
          itens: 1,
          valor: 279.99,
          quando: "há 5h",
        },
        {
          id: 1991188450,
          nome: "Juliana Martins",
          email: "jumartins91@gmail.com",
          fone: "+5511996418830",
          itens: 1,
          valor: 489.99,
          quando: "há 9h",
        },
        {
          id: 1991174023,
          nome: "Beatriz Lopes",
          email: "bia.lopes22@gmail.com",
          fone: "+5511991557384",
          itens: 3,
          valor: 259.97,
          quando: "há 14h",
        },
        {
          id: 1991150591,
          nome: "Flávia Dos Santos Lourenço",
          email: "flavuxinh4@gmail.com",
          fone: "+5511984339602",
          itens: 1,
          valor: 199.99,
          quando: "há 18h",
        },
        {
          id: 1991132748,
          nome: "Patrícia Gomes",
          email: "patgomes.sp@gmail.com",
          fone: "+5511982016645",
          itens: 2,
          valor: 339.98,
          quando: "há 26h",
        },
        {
          id: 1991118305,
          nome: "Renata Carvalho",
          email: "re.carvalho@outlook.com",
          fone: "+5511999023318",
          itens: 1,
          valor: 369.99,
          quando: "há 31h",
        },
        {
          id: 1991104976,
          nome: "Aline Teixeira",
          email: "alineteix@gmail.com",
          fone: "+5511997440521",
          itens: 2,
          valor: 229.98,
          quando: "há 38h",
        },
        {
          id: 1991090214,
          nome: "Larissa Ferreira",
          email: "lari.ferreira@gmail.com",
          fone: "+5511983907716",
          itens: 1,
          valor: 169.99,
          quando: "há 45h",
        },
      ],
    },
    {
      id: "estoque",
      tag: "Estoque",
      icone: "box",
      titulo: "3 produtos vão zerar o estoque em até 7 dias",
      resumo:
        "Monitoro todos os produtos e projeto quando o estoque acaba com base na média de vendas dos últimos 30 dias.",
      valorRisco: 4110,
      valorLabel: "de receita em risco",
      insight:
        "Reponha o Shape Maple Koloni Santa primeiro — é a maior receita em risco e o fornecedor leva 5 dias úteis.",
      cta: "Marcar reposto",
      tipo: "feito",
      potencial: 4110,
      detalheCta: "Ver detalhes",
      detalheTitulo: "Produtos com estoque crítico · próximos 7 dias",
      produtos: [
        {
          slot: "stk-shape-santa",
          img: stkShape,
          nome: "Shape Maple Koloni Santa",
          variante: '8.0" · Maple',
          estoque: 14,
          mediaDia: "2,4",
          zeraEm: "6 dias",
          preco: 279.99,
          receitaRisco: 2400,
        },
        {
          slot: "stk-tenis-flow",
          img: stkTenis,
          nome: "Tênis Tesla Flow Black/White",
          variante: "Tam. 41 · Black/White",
          estoque: 11,
          mediaDia: "2,6",
          zeraEm: "4 dias",
          preco: 309.99,
          receitaRisco: 1300,
        },
        {
          slot: "stk-calca-cargo",
          img: stkCalca,
          nome: "Calça Koloni Cargo Jeans Estonado",
          variante: "Tam. 42 · Jeans",
          estoque: 8,
          mediaDia: "2,6",
          zeraEm: "3 dias",
          preco: 199.99,
          receitaRisco: 410,
        },
      ],
    },
    {
      id: "anuncios",
      tag: "Anúncios",
      icone: "chart",
      titulo: "2 de 4 campanhas ativas perderam performance",
      resumo:
        "Monitoro todas as campanhas ativas e comparo o ROAS real (descontando custos) com o histórico das últimas semanas.",
      valorRisco: 2100,
      valorLabel: "de prejuízo no mês",
      insight:
        "Pause a Meta “Drop de Inverno” (ROAS real 2,9, abaixo do breakeven) e reduza o Shopping Geral — só isso estanca R$ 2.100/mês.",
      cta: "Marcar pausada",
      tipo: "feito",
      potencial: 2100,
      detalheCta: "Mais detalhes",
      detalheTitulo: "Campanhas que perderam performance",
      colunas: ["Campanha", "Investido", "ROAS antes", "ROAS agora", "Lucro/mês"],
      linhas: [
        ["Meta · Drop de Inverno", "R$ 4.800", "4,2", "2,9", -1900],
        ["Google · Shopping Geral", "R$ 1.800", "3,1", "2,0", -200],
      ],
      valorCol: 4,
    },
  ],
};

// Série de lucro dos últimos 30 dias (mock realista, termina em queda ~14%)
const lucroSerie = (function () {
  const base = [
    520, 540, 505, 560, 575, 540, 500, 470, 495, 520, 540, 560, 530, 505, 480, 460, 440, 475, 500,
    520, 490, 455, 430, 410, 440, 470, 450, 420, 395, 373,
  ];
  return base.map((v, i) => ({ dia: i + 1, valor: v }));
})();

// composição do lucro (waterfall)
const waterfall: WaterfallStep[] = [
  { rotulo: "Receita bruta", valor: 53000, tipo: "base" },
  // dedução da receita bruta (cupons) — lado da receita, nunca custo.
  { rotulo: "Descontos", valor: -3000, tipo: "sub" },
  { rotulo: "CMV", valor: -21000, tipo: "sub" },
  { rotulo: "Frete", valor: -2640, tipo: "sub" },
  { rotulo: "Imposto", valor: -2600, tipo: "sub" },
  { rotulo: "Embalagens", valor: -1560, tipo: "sub" },
  // marco: receita líquida − custos variáveis (CMV+frete+imposto+embalagem), antes da mídia.
  { rotulo: "Margem de contribuição", valor: 22200, tipo: "subtotal" },
  { rotulo: "Anúncios", valor: -11000, tipo: "sub" },
  { rotulo: "Lucro das vendas", valor: 11200, tipo: "total" },
];

// ---- Marketing (Épico 2) ----
const marketing = {
  janelaAtribuicao: "7 dias clique · 1 dia visualização",
  gastoTotal: 11000,
  receitaAtribuida: 43280,
  mer: 4.5, // receita total / gasto total
  canais: [
    {
      id: "meta",
      canal: "Meta Ads",
      gasto: 8000,
      receitaAtrib: 29640,
      roasReal: 3.7,
      roasPlat: 5.6,
      campanhasAtivas: 2,
    },
    {
      id: "gads",
      canal: "Google Ads",
      gasto: 3000,
      receitaAtrib: 13640,
      roasReal: 4.5,
      roasPlat: 5.1,
      campanhasAtivas: 2,
    },
  ],
  integracoes: [
    {
      id: "meta",
      glyph: "meta",
      nome: "Meta Ads",
      conta: "Koloni Skate Shop · act_2938107",
      status: "ok",
      sync: "há 12 min",
      // health-check preventivo (decisions.md §18): o tagging é verificado na
      // conexão — é o que mantém o mapa de campanhas 100% automático.
      checks: [{ rotulo: "Tagging automático (utm_id) nos anúncios ativos", ok: true }],
    },
    {
      id: "gads",
      glyph: "gads",
      nome: "Google Ads",
      conta: "koloni-skateshop · 734-221-8830",
      status: "ok",
      sync: "há 18 min",
      checks: [{ rotulo: "Conta vinculada ao GA4 · auto-tagging ativo", ok: true }],
    },
    {
      id: "ga",
      glyph: "ga",
      nome: "Google Analytics 4",
      conta: "koloniskateshop.com · GA4",
      status: "atencao",
      sync: "há 3 dias",
      aviso: "Token expirou — reconecte para atualizar os canais de aquisição.",
    },
  ],
};

// ---- Auditoria de atribuição por pedido (decisions.md §9 + §18) ----
// Amostra auditável: cada pedido com os sinais de last-touch que carregou, e o
// canal que o Lucraa creditou — a prova, pedido a pedido, do ROAS real. Os campos
// resolvidos (canal/paga/degrau) são DERIVADOS por resolverAtribuicao — nunca
// digitados (mesmo padrão de faixaDetectada); o nome da campanha vem do join
// utm_id ↔ ads.campanhas.externalId (§18). É AMOSTRA, não a base completa do ROAS
// (centenas de pedidos) — não somar contra receitaAtribuida (database-schema §6).
const sin = (p: Partial<SinaisAtribuicao>): SinaisAtribuicao => ({
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmId: null,
  channelHint: null,
  ...p,
});

interface PedidoAuditBase {
  externalId: string;
  numero: string;
  data: string;
  valor: number;
  ultimoCliqueEm: string | null;
  sinais: SinaisAtribuicao;
}

// Resolve canal/paga/degrau pela regra (§9+§18) e ata o nome da campanha pelo
// utm_id — garantindo, por construção, que a amostra nunca contradiz a cascata.
function auditar(b: PedidoAuditBase): PedidoAuditavel {
  const at = resolverAtribuicao(b.sinais);
  const campanha =
    b.sinais.utmId != null
      ? (ads.campanhas.find((c) => c.externalId === b.sinais.utmId)?.nome ?? null)
      : null;
  return { ...b, ...at, campanha };
}

const auditoria = {
  janelaPlataforma: marketing.janelaAtribuicao,
  pedidos: [
    // Google pago — gclid resolve nativo (§18 r1); o GA4 reporta source/medium=google/cpc.
    auditar({
      externalId: "1182051",
      numero: "#1051",
      data: "2026-06-10T08:52",
      valor: 309.99,
      ultimoCliqueEm: "2026-06-10T08:47",
      sinais: sin({
        utmSource: "google",
        utmMedium: "cpc",
        utmCampaign: "Search Marca",
        utmId: "21487326544",
      }),
    }),
    auditar({
      externalId: "1182049",
      numero: "#1049",
      data: "2026-06-09T19:40",
      valor: 159.99,
      ultimoCliqueEm: "2026-06-09T19:33",
      sinais: sin({
        utmSource: "google",
        utmMedium: "cpc",
        utmCampaign: "Shopping Geral",
        utmId: "21490112837",
      }),
    }),
    // Social orgânico — utm_source=instagram SEM medium pago: NÃO credita a Meta
    // (§18 r3, o erro que o produto existe para denunciar). Aparece como Social.
    auditar({
      externalId: "1182048",
      numero: "#1048",
      data: "2026-06-09T16:48",
      valor: 89.99,
      ultimoCliqueEm: "2026-06-09T13:05",
      sinais: sin({ utmSource: "instagram", utmMedium: "social" }),
    }),
    // Meta pago — utm_id casa a campanha "Drop de Inverno" (join determinístico §18 r2).
    auditar({
      externalId: "1182047",
      numero: "#1047",
      data: "2026-06-09T14:41",
      valor: 279.99,
      ultimoCliqueEm: "2026-06-09T14:32",
      sinais: sin({
        utmSource: "ig",
        utmMedium: "paid_social",
        utmCampaign: "Drop de Inverno",
        utmId: "23851840921660123",
      }),
    }),
    auditar({
      externalId: "1182043",
      numero: "#1043",
      data: "2026-06-08T20:19",
      valor: 149.99,
      ultimoCliqueEm: "2026-06-08T20:11",
      sinais: sin({
        utmSource: "instagram",
        utmMedium: "paid_social",
        utmCampaign: "Drop de Inverno",
        utmId: "23851840921660123",
      }),
    }),
    auditar({
      externalId: "1182041",
      numero: "#1041",
      data: "2026-06-08T11:27",
      valor: 309.99,
      ultimoCliqueEm: "2026-06-08T11:02",
      sinais: sin({
        utmSource: "facebook",
        utmMedium: "paid_social",
        utmCampaign: "Retargeting",
        utmId: "23851877410230123",
      }),
    }),
    // Sem UTM — a plataforma informou o canal (channelHint). Não prova clique pago.
    auditar({
      externalId: "1182038",
      numero: "#1038",
      data: "2026-06-07T18:52",
      valor: 99.99,
      ultimoCliqueEm: "2026-06-07T18:40",
      sinais: sin({ channelHint: "instagram" }),
    }),
    // Direto — sem UTM e sem clique registrado (dark traffic → (direct)/(none)).
    auditar({
      externalId: "1182036",
      numero: "#1036",
      data: "2026-06-07T07:55",
      valor: 89.99,
      ultimoCliqueEm: null,
      sinais: sin({}),
    }),
    // Meta pago SEM utm_id (anúncio antigo sem tag) — degradê §18 r4: credita o
    // canal Meta, mas sem campanha; soma no balde "gasto sem campanha identificada".
    auditar({
      externalId: "1182033",
      numero: "#1033",
      data: "2026-06-06T09:20",
      valor: 149.99,
      ultimoCliqueEm: "2026-06-06T09:14",
      sinais: sin({ utmSource: "ig", utmMedium: "paid_social" }),
    }),
    // Orgânico de busca — utm_source=google com medium=organic não credita mídia.
    auditar({
      externalId: "1182031",
      numero: "#1031",
      data: "2026-06-05T22:31",
      valor: 169.99,
      ultimoCliqueEm: "2026-06-05T22:14",
      sinais: sin({ utmSource: "google", utmMedium: "organic" }),
    }),
    // E-mail marketing — creditado ao canal e-mail, nunca à mídia paga.
    auditar({
      externalId: "1182028",
      numero: "#1028",
      data: "2026-06-04T10:20",
      valor: 199.99,
      ultimoCliqueEm: "2026-06-04T10:02",
      sinais: sin({ utmSource: "newsletter", utmMedium: "email", utmCampaign: "Drop Junho" }),
    }),
  ],
};

// ---- Recompra & LTV (Épico 3) ----
// Coortes: mês da 1ª compra · ltv = receita acumulada POR CLIENTE em M0..Mn · recompra = % que recomprou até Mn
const coortes = [
  {
    mes: "Jan/26",
    novos: 136,
    gastoMkt: 12920,
    ltv: [130, 200, 280, 360, 395, 415],
    recompra: [0, 18, 27, 34, 37, 39],
  },
  {
    mes: "Fev/26",
    novos: 118,
    gastoMkt: 12100,
    ltv: [122, 178, 240, 302, 330],
    recompra: [0, 15, 23, 29, 32],
  },
  {
    mes: "Mar/26",
    novos: 125,
    gastoMkt: 11540,
    ltv: [138, 195, 255, 295],
    recompra: [0, 16, 24, 28],
  },
  { mes: "Abr/26", novos: 112, gastoMkt: 11760, ltv: [128, 175, 220], recompra: [0, 14, 21] },
  { mes: "Mai/26", novos: 130, gastoMkt: 13040, ltv: [135, 183], recompra: [0, 13] },
  { mes: "Jun/26", novos: 47, gastoMkt: 4980, ltv: [125], recompra: [0], imaturo: true },
];

const clientes = [
  {
    nome: "Matheus Oliveira",
    primeira: "12/jan/26",
    coorte: "Jan/26",
    pedidos: 3,
    receita: 429.97,
    ultima: "09/abr/26",
    intervaloDias: 44,
    canal: "Meta Ads",
    timeline: [
      {
        data: "12/jan/26",
        valor: 79.99,
        itens: "Camiseta Huf Ice Dice",
        canal: "Meta Ads · campanha Drop de Inverno",
      },
      { data: "03/mar/26", valor: 199.99, itens: "Moletom Volcom Iconic Preto", canal: "Direto" },
      {
        data: "09/abr/26",
        valor: 149.99,
        itens: "Calça Baggy Koloni Vintage Black",
        canal: "E-mail · cupom volta10",
      },
    ],
  },
  {
    nome: "Mariana Souza Lima",
    primeira: "04/nov/25",
    coorte: "Nov/25",
    pedidos: 6,
    receita: 1829.93,
    ultima: "28/mai/26",
    intervaloDias: 41,
    canal: "Orgânico",
    timeline: [
      { data: "04/nov/25", valor: 279.99, itens: "Shape Maple Koloni Santa", canal: "Orgânico" },
      { data: "12/dez/25", valor: 489.99, itens: "Tênis Hocks Bold Petit Poa", canal: "Direto" },
      { data: "22/jan/26", valor: 309.99, itens: "Tênis Tesla Flow Black/White", canal: "E-mail" },
      {
        data: "03/mar/26",
        valor: 199.99,
        itens: "Calça Koloni Carpinteiro Estonado",
        canal: "Direto",
      },
      { data: "15/abr/26", valor: 279.99, itens: "Shape Maple Koloni Santa", canal: "Direto" },
      {
        data: "28/mai/26",
        valor: 269.98,
        itens: "Camiseta Huf + Shape Dropdead Awake",
        canal: "E-mail",
      },
    ],
  },
  {
    nome: "Juliana Martins",
    primeira: "15/fev/26",
    coorte: "Fev/26",
    pedidos: 4,
    receita: 939.96,
    ultima: "19/mai/26",
    intervaloDias: 31,
    canal: "Meta Ads",
    timeline: [
      {
        data: "15/fev/26",
        valor: 79.99,
        itens: "Camiseta Huf Ice Dice",
        canal: "Meta Ads · Retargeting",
      },
      {
        data: "18/mar/26",
        valor: 399.99,
        itens: "Skate Montado Hondar Gold Rush",
        canal: "Direto",
      },
      { data: "21/abr/26", valor: 179.99, itens: "Shoulder Bag Plano C", canal: "E-mail" },
      { data: "19/mai/26", valor: 279.99, itens: "Shape Maple Koloni Santa", canal: "Direto" },
    ],
  },
  {
    nome: "Renata Carvalho",
    primeira: "08/jan/26",
    coorte: "Jan/26",
    pedidos: 2,
    receita: 499.97,
    ultima: "26/mar/26",
    intervaloDias: 77,
    canal: "Google Ads",
    timeline: [
      {
        data: "08/jan/26",
        valor: 89.99,
        itens: "Meia Stance Baron",
        canal: "Google Ads · Search Marca",
      },
      {
        data: "26/mar/26",
        valor: 409.98,
        itens: "Tênis Tesla Flow + Camiseta New Era",
        canal: "Direto",
      },
    ],
  },
  {
    nome: "Camila Rocha",
    primeira: "22/mar/26",
    coorte: "Mar/26",
    pedidos: 2,
    receita: 429.98,
    ultima: "30/mai/26",
    intervaloDias: 69,
    canal: "Social",
    timeline: [
      {
        data: "22/mar/26",
        valor: 149.99,
        itens: "Chinelo Slide Hocks Filigrama",
        canal: "Social · Instagram",
      },
      { data: "30/mai/26", valor: 279.99, itens: "Shape Maple Koloni Santa", canal: "Direto" },
    ],
  },
  {
    nome: "Beatriz Lopes",
    primeira: "05/abr/26",
    coorte: "Abr/26",
    pedidos: 1,
    receita: 239.97,
    ultima: "05/abr/26",
    intervaloDias: null,
    canal: "Meta Ads",
    timeline: [
      {
        data: "05/abr/26",
        valor: 239.97,
        itens: "Camiseta Huf Ice Dice ×3",
        canal: "Meta Ads · Drop de Inverno",
      },
    ],
  },
  {
    nome: "Patrícia Gomes",
    primeira: "11/mai/26",
    coorte: "Mai/26",
    pedidos: 1,
    receita: 379.98,
    ultima: "11/mai/26",
    intervaloDias: null,
    canal: "Google Ads",
    timeline: [
      {
        data: "11/mai/26",
        valor: 379.98,
        itens: "Shape Koloni Santa + Camiseta New Era",
        canal: "Google Ads · Shopping",
      },
    ],
  },
  {
    nome: "Fernanda Almeida",
    primeira: "19/jun/26",
    coorte: "Jun/26",
    pedidos: 1,
    receita: 309.99,
    ultima: "19/jun/26",
    intervaloDias: null,
    canal: "Meta Ads",
    timeline: [
      {
        data: "19/jun/26",
        valor: 309.99,
        itens: "Tênis Tesla Flow Black/White",
        canal: "Meta Ads · Drop de Inverno",
      },
    ],
  },
];

// ---- Custos de produto (Épico 1) ----
const custos = {
  totalSkus: 443,
  comCusto: 420,
  semCusto: 23,
  // Defaults da loja: SKU com imposto/embalagem `null` HERDA estes valores na
  // margem real (decisions.md §24) — só vira número quando o SKU sobrescreve.
  // Por isso a maioria dos SKUs abaixo tem `imposto: null` (regime tributário
  // uniforme) e `embalagem: null` quando empaca no padrão. Simplificação de
  // demo: valor único; o real é por categoria, no Tenant (database-schema.md §6).
  impostoDefault: 0.07, // Simples Nacional — default da loja
  embalagemDefault: 6.5, // caixa + fita + adesivo padrão (R$) — default da loja
  rateioFixo: 0.04, // % sobre receita (configuração da loja)
  // CMV médio da loja (fração do preço) para a margem ESTIMADA de SKUs sem
  // custo — configurável pelo lojista; exibição apenas, nunca persiste
  // (decisions.md §13). NÃO entra na margem real (diferente dos defaults acima).
  // Revenda de marca (Huf/New Era/Volcom) puxa o CMV médio para cima — coerente
  // com o CMV do waterfall (21.000 ÷ 50.000).
  cmvEstimadoPct: 0.42,
  amostra: [
    {
      pid: "P-1043",
      sku: "TFW-40-BLKWHT",
      produto: "Tênis Tesla Flow Black/White",
      variante: "40 · Black/White",
      preco: 309.99,
      cmv: 132.5,
      imposto: null,
      embalagem: null,
      validFrom: "01/mai/26",
    },
    {
      pid: "P-1043",
      sku: "TFW-42-BLKWHT",
      produto: "Tênis Tesla Flow Black/White",
      variante: "42 · Black/White",
      preco: 309.99,
      cmv: 132.5,
      imposto: null,
      embalagem: null,
      validFrom: "01/mai/26",
    },
    {
      pid: "P-0871",
      sku: "CHF-M-ICEDICE",
      produto: "Camiseta Huf Ice Dice",
      variante: "M · Preto",
      preco: 79.99,
      cmv: 58.1,
      imposto: null,
      embalagem: 2.1,
      validFrom: "14/abr/26",
    },
    {
      pid: "P-1198",
      sku: "SHK-80-SANTA",
      produto: "Shape Maple Koloni Santa",
      variante: '8.0" · Maple',
      preco: 279.99,
      cmv: 124.3,
      imposto: null,
      embalagem: 3.4,
      validFrom: "01/mai/26",
    },
    {
      pid: "P-1304",
      sku: "KCJ-42-JEANS",
      produto: "Calça Koloni Cargo Jeans Estonado",
      variante: "42 · Jeans",
      preco: 199.99,
      cmv: null,
      imposto: null,
      embalagem: null,
      validFrom: null,
    },
  ],
};

// ---- Catálogo (Gestão de produtos) ----
// Página exibida na demo. Invariantes (ver koloni-skateshop.test.ts): todo SKU da
// amostra de custos aparece aqui com os MESMOS números; preço bate com a curva
// ABC. Dados de ML/ERP foram estacionados — ver docs/enhancements.md
// (tag demo-completa-2026-06-11).
const SYNC_NS = "há 12 min";

const catalogo = {
  conexoes: [
    {
      plataforma: "nuvemshop" as const,
      lojaNome: "Koloni Skate Shop",
      ultimaSync: SYNC_NS,
      skus: 443,
    },
  ],
  fontesCusto: [],
  produtos: [
    {
      pid: "P-1043",
      skuInterno: "TFW-40-BLKWHT",
      nome: "Tênis Tesla Flow Black/White",
      variante: "40 · Black/White",
      preco: 309.99,
      custo: {
        cmv: 132.5,
        imposto: null,
        embalagem: null,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "01/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771204",
          preco: 309.99,
          estoque: 6,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1043",
      skuInterno: "TFW-42-BLKWHT",
      nome: "Tênis Tesla Flow Black/White",
      variante: "42 · Black/White",
      preco: 309.99,
      custo: {
        cmv: 132.5,
        imposto: null,
        embalagem: null,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "01/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771205",
          preco: 309.99,
          estoque: 5,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-0871",
      skuInterno: "CHF-M-ICEDICE",
      nome: "Camiseta Huf Ice Dice",
      variante: "M · Preto",
      preco: 79.99,
      custo: {
        cmv: 58.1,
        imposto: null,
        embalagem: 2.1,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "14/abr/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-770318",
          preco: 79.99,
          estoque: 41,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1198",
      skuInterno: "SHK-80-SANTA",
      nome: "Shape Maple Koloni Santa",
      variante: '8.0" · Maple',
      preco: 279.99,
      custo: {
        cmv: 124.3,
        imposto: null,
        embalagem: 3.4,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "01/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771877",
          preco: 279.99,
          estoque: 14,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1304",
      skuInterno: "KCJ-42-JEANS",
      nome: "Calça Koloni Cargo Jeans Estonado",
      variante: "42 · Jeans",
      preco: 199.99,
      custo: {
        cmv: null,
        imposto: null,
        embalagem: null,
        fonte: null,
        fonteConexao: null,
        validFrom: null,
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-772401",
          preco: 199.99,
          estoque: 8,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-0954",
      skuInterno: "SMH-80-GOLD",
      nome: "Skate Montado Hondar Iniciante Gold Rush",
      variante: '8.0" · Completo',
      preco: 399.99,
      custo: {
        cmv: 204.5,
        imposto: null,
        embalagem: 4.8,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "03/jun/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-770992",
          preco: 399.99,
          estoque: 9,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1122",
      skuInterno: "THB-41-POA",
      nome: "Tênis Hocks Bold Petit Poa",
      variante: "41 · Petit Poa",
      preco: 489.99,
      custo: {
        cmv: 232.4,
        imposto: null,
        embalagem: 3.4,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "01/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771560",
          preco: 489.99,
          estoque: 7,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1276",
      skuInterno: "MVI-M-PRETO",
      nome: "Moletom Volcom Iconic Preto",
      variante: "M · Preto",
      preco: 199.99,
      custo: {
        cmv: 91.8,
        imposto: null,
        embalagem: 3.8,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "28/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-772188",
          preco: 199.99,
          estoque: 9,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1187",
      skuInterno: "CBK-40-VINTAGE",
      nome: "Calça Baggy Koloni Vintage Black",
      variante: "40 · Vintage Black",
      preco: 149.99,
      custo: {
        cmv: 67.4,
        imposto: null,
        embalagem: null,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "02/jun/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771820",
          preco: 149.99,
          estoque: 10,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-1233",
      skuInterno: "CCT-39-PRETO",
      nome: "Tênis Converse Chuck Taylor Cano Alto",
      variante: "39 · Preto",
      preco: 289.99,
      custo: {
        cmv: 148.2,
        imposto: null,
        embalagem: 2.8,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "01/mai/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-771944",
          preco: 289.99,
          estoque: 13,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-0640",
      skuInterno: "CSH-4142-PRETO",
      nome: "Chinelo Slide Hocks Filigrama",
      variante: "41/42 · Preto",
      preco: 149.99,
      custo: {
        cmv: 61.2,
        imposto: null,
        embalagem: 1.4,
        fonte: "manual" as const,
        fonteConexao: null,
        validFrom: "14/abr/26",
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-769105",
          preco: 149.99,
          estoque: 19,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
    {
      pid: "P-0702",
      skuInterno: "MST-U-BARON",
      nome: "Meia Stance Baron",
      variante: "Único · Baron",
      preco: 89.99,
      custo: {
        cmv: null,
        imposto: null,
        embalagem: null,
        fonte: null,
        fonteConexao: null,
        validFrom: null,
      },
      listagens: [
        {
          plataforma: "nuvemshop" as const,
          externalId: "NS-769488",
          preco: 89.99,
          estoque: 44,
          vinculo: "sku_auto" as const,
          ultimaSync: SYNC_NS,
        },
      ],
    },
  ],
  naoVinculadas: [],
};

// ---- Base da Curva ABC (faturamento 30d por produto) ----
// [nome, fat30d, estoque, preco, semVendasDias] — antes hardcoded na tela.
// Nomes e preços REAIS do catálogo da koloniskateshop.com (jun/26).
const ABC_BASE: [string, number, number, number, number][] = [
  ["Tênis Tesla Flow Black/White", 14260, 11, 309.99, 0],
  ["Camiseta Huf Ice Dice", 13200, 98, 79.99, 0],
  ["Shape Maple Koloni Santa", 9520, 14, 279.99, 0],
  ["Skate Montado Hondar Iniciante Gold Rush", 7200, 9, 399.99, 0],
  ["Tênis Hocks Bold Petit Poa", 4410, 7, 489.99, 0],
  ["Calça Baggy Koloni Vintage Black", 3450, 25, 149.99, 0],
  ["Moletom Volcom Iconic Preto", 2800, 18, 199.99, 0],
  ["Tênis Converse Chuck Taylor Cano Alto", 2030, 12, 289.99, 0],
  ["Calça Cargo Koloni Sarja Off-White", 1760, 16, 159.99, 0],
  ["Shape Chaze Melow Silas Ribeiro", 1500, 10, 299.99, 0],
  ["Camiseta New Era Vintage Class Verde", 1100, 22, 99.99, 0],
  ["Chinelo Slide Hocks Filigrama", 750, 19, 149.99, 0],
  ["Calça Koloni Cargo Jeans Estonado", 600, 8, 199.99, 0],
  ["Tênis Ous Imigrante Série X Azul", 480, 5, 479.99, 38],
  ["Shoulder Bag Plano C", 360, 9, 179.99, 34],
  ["Shape Dropdead Marfim Série Awake", 190, 12, 189.99, 41],
  ["Meia Stance Baron", 180, 44, 89.99, 47],
  ["Calça Reta Jeans Koloni Azul", 170, 31, 169.99, 52],
];

const abcBase = ABC_BASE.map(([nome, fat, estoque, preco, semVendas]) => ({
  nome,
  fat,
  estoque,
  preco,
  semVendas,
}));

// ---- Rankings top 10 (mês corrente) ----
// Unidades e lucro bruto coerentes com o catálogo da curva ABC.
// Chaveados pelos mesmos ids de derivados.lucroPeriodos — os cards de top 10
// obedecem ao filtro de período da tela Lucro/Início. "este-mes" é a referência
// (mesmos números do catálogo/curva ABC); os demais mantêm o lucro bruto
// unitário de cada produto e trocam a ordem para a demo parecer viva.
const rankings = {
  hoje: {
    categorias: [
      { nome: "Camisetas", vendas: 6, faturamento: 500 },
      { nome: "Tênis", vendas: 2, faturamento: 620 },
      { nome: "Calças", vendas: 1, faturamento: 150 },
      { nome: "Shapes", vendas: 1, faturamento: 280 },
      { nome: "Skate montado", vendas: 1, faturamento: 400 },
      { nome: "Moletons", vendas: 1, faturamento: 200 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 5, lucroBruto: 19 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 2, lucroBruto: 116 },
      { nome: "Shape Maple Koloni Santa", vendas: 1, lucroBruto: 45 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 1, lucroBruto: 55 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 1, lucroBruto: 97 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 1, lucroBruto: 46 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 1, lucroBruto: 28 },
    ],
  },
  "7d": {
    categorias: [
      { nome: "Camisetas", vendas: 47, faturamento: 3820 },
      { nome: "Tênis", vendas: 16, faturamento: 5510 },
      { nome: "Shapes", vendas: 10, faturamento: 2900 },
      { nome: "Calças", vendas: 10, faturamento: 1560 },
      { nome: "Skate montado", vendas: 5, faturamento: 2000 },
      { nome: "Moletons", vendas: 4, faturamento: 800 },
      { nome: "Chinelos", vendas: 1, faturamento: 150 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 44, lucroBruto: 165 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 12, lucroBruto: 696 },
      { nome: "Shape Maple Koloni Santa", vendas: 9, lucroBruto: 402 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 6, lucroBruto: 330 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 5, lucroBruto: 483 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 4, lucroBruto: 184 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 3, lucroBruto: 84 },
      { nome: "Calça Cargo Koloni Sarja Off-White", vendas: 3, lucroBruto: 174 },
      { nome: "Tênis Hocks Bold Petit Poa", vendas: 2, lucroBruto: 293 },
      { nome: "Tênis Converse Chuck Taylor Cano Alto", vendas: 2, lucroBruto: 104 },
    ],
  },
  "30d": {
    categorias: [
      { nome: "Camisetas", vendas: 205, faturamento: 16660 },
      { nome: "Tênis", vendas: 72, faturamento: 24080 },
      { nome: "Shapes", vendas: 46, faturamento: 12820 },
      { nome: "Calças", vendas: 43, faturamento: 6760 },
      { nome: "Skate montado", vendas: 21, faturamento: 8400 },
      { nome: "Moletons", vendas: 16, faturamento: 3200 },
      { nome: "Chinelos", vendas: 6, faturamento: 900 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 192, lucroBruto: 722 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 54, lucroBruto: 3132 },
      { nome: "Shape Maple Koloni Santa", vendas: 40, lucroBruto: 1788 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 27, lucroBruto: 1485 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 21, lucroBruto: 2030 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 16, lucroBruto: 736 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 13, lucroBruto: 364 },
      { nome: "Calça Cargo Koloni Sarja Off-White", vendas: 13, lucroBruto: 754 },
      { nome: "Tênis Hocks Bold Petit Poa", vendas: 10, lucroBruto: 1467 },
      { nome: "Tênis Converse Chuck Taylor Cano Alto", vendas: 8, lucroBruto: 416 },
    ],
  },
  "este-mes": {
    categorias: [
      { nome: "Camisetas", vendas: 176, faturamento: 14300 },
      { nome: "Tênis", vendas: 62, faturamento: 20700 },
      { nome: "Shapes", vendas: 39, faturamento: 11020 },
      { nome: "Calças", vendas: 37, faturamento: 5810 },
      { nome: "Skate montado", vendas: 18, faturamento: 7200 },
      { nome: "Moletons", vendas: 14, faturamento: 2800 },
      { nome: "Chinelos", vendas: 5, faturamento: 750 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 165, lucroBruto: 620 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 46, lucroBruto: 2668 },
      { nome: "Shape Maple Koloni Santa", vendas: 34, lucroBruto: 1520 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 23, lucroBruto: 1265 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 18, lucroBruto: 1740 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 14, lucroBruto: 644 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 11, lucroBruto: 308 },
      { nome: "Calça Cargo Koloni Sarja Off-White", vendas: 11, lucroBruto: 638 },
      { nome: "Tênis Hocks Bold Petit Poa", vendas: 9, lucroBruto: 1320 },
      { nome: "Tênis Converse Chuck Taylor Cano Alto", vendas: 7, lucroBruto: 364 },
    ],
  },
  "mes-passado": {
    categorias: [
      { nome: "Camisetas", vendas: 215, faturamento: 17480 },
      { nome: "Tênis", vendas: 76, faturamento: 25400 },
      { nome: "Shapes", vendas: 48, faturamento: 13400 },
      { nome: "Calças", vendas: 45, faturamento: 7100 },
      { nome: "Skate montado", vendas: 22, faturamento: 8800 },
      { nome: "Moletons", vendas: 17, faturamento: 3400 },
      { nome: "Chinelos", vendas: 7, faturamento: 1050 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 201, lucroBruto: 755 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 56, lucroBruto: 3248 },
      { nome: "Shape Maple Koloni Santa", vendas: 42, lucroBruto: 1878 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 28, lucroBruto: 1540 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 22, lucroBruto: 2127 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 17, lucroBruto: 782 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 14, lucroBruto: 392 },
      { nome: "Calça Cargo Koloni Sarja Off-White", vendas: 13, lucroBruto: 754 },
      { nome: "Tênis Hocks Bold Petit Poa", vendas: 11, lucroBruto: 1613 },
      { nome: "Tênis Converse Chuck Taylor Cano Alto", vendas: 9, lucroBruto: 468 },
    ],
  },
  "90d": {
    categorias: [
      { nome: "Camisetas", vendas: 595, faturamento: 48400 },
      { nome: "Tênis", vendas: 210, faturamento: 70300 },
      { nome: "Shapes", vendas: 131, faturamento: 35600 },
      { nome: "Calças", vendas: 124, faturamento: 19600 },
      { nome: "Skate montado", vendas: 61, faturamento: 24400 },
      { nome: "Moletons", vendas: 47, faturamento: 9400 },
      { nome: "Chinelos", vendas: 18, faturamento: 2700 },
    ],
    produtos: [
      { nome: "Camiseta Huf Ice Dice", vendas: 558, lucroBruto: 2097 },
      { nome: "Tênis Tesla Flow Black/White", vendas: 156, lucroBruto: 9048 },
      { nome: "Shape Maple Koloni Santa", vendas: 115, lucroBruto: 5142 },
      { nome: "Calça Baggy Koloni Vintage Black", vendas: 78, lucroBruto: 4290 },
      { nome: "Skate Montado Hondar Iniciante Gold Rush", vendas: 61, lucroBruto: 5897 },
      { nome: "Moletom Volcom Iconic Preto", vendas: 47, lucroBruto: 2162 },
      { nome: "Camiseta New Era Vintage Class Verde", vendas: 37, lucroBruto: 1036 },
      { nome: "Calça Cargo Koloni Sarja Off-White", vendas: 37, lucroBruto: 2146 },
      { nome: "Tênis Hocks Bold Petit Poa", vendas: 30, lucroBruto: 4400 },
      { nome: "Tênis Converse Chuck Taylor Cano Alto", vendas: 24, lucroBruto: 1248 },
    ],
  },
};

// ---- Plano único com faixas por faturamento (decisions.md §20 + §25) ----
// Tudo incluso em todas as faixas; o preço acompanha o porte da loja e fica
// abaixo da âncora sales-led da categoria nas zonas contestadas (§25 — split
// em R$ 500k). Faixa detectada na conexão (média 90d), reavaliada só na
// renovação — downgrade automático. Acessos: 3 inclusos em toda faixa,
// assento extra é add-on (R$ 50/mês). Revisão de Lucro (serviço gravado,
// nunca gate de software) nas faixas 4–5. Coerência: koloni-skateshop.test.ts.
const plano = {
  nome: "Lucraa+",
  destaque:
    "A mesma plataforma em todas as faixas — nenhum recurso fica atrás de upgrade. Só o preço acompanha o porte da loja.",
  acessosInclusos: 3,
  precoAcessoAdicional: 50,
  recursos: [
    {
      icone: "coins",
      titulo: "Profit dashboard",
      desc: "Da receita ao lucro das vendas, por período e categoria",
    },
    {
      icone: "box",
      titulo: "Gestão de produtos",
      desc: "Catálogo único com custos por SKU — manual vence sync",
    },
    {
      icone: "chart",
      titulo: "Curva ABC",
      desc: "Concentração de receita e capital parado em estoque",
    },
    {
      icone: "megaphone",
      titulo: "Marketing com ROAS real",
      desc: "Gasto, MER e lucro por campanha — descontando custos",
    },
    {
      icone: "target",
      titulo: "Breakeven por produto",
      desc: "Qual produto aguenta tráfego pago — e qual não",
    },
    {
      icone: "repeat",
      titulo: "Recompra & LTV",
      desc: "Coortes, CAC e o mês em que o investimento se paga",
    },
    {
      icone: "search",
      titulo: "Monitores 24/7",
      desc: "Vendas, estoque e anúncios — cada alerta em reais",
    },
    {
      icone: "spark",
      titulo: "Recomendação do dia",
      desc: "Uma orientação clara toda manhã, direto no painel",
    },
    {
      icone: "link",
      titulo: "Integração Nuvemshop",
      desc: "Vendas e produtos sempre sincronizados",
    },
  ],
  faixas: [
    {
      id: "f1" as const,
      faturamentoMin: 0,
      faturamentoMax: 25_000,
      precoMensal: 197,
      revisaoLucro: null,
    },
    {
      id: "f2" as const,
      faturamentoMin: 25_000,
      faturamentoMax: 80_000,
      precoMensal: 397,
      revisaoLucro: null,
    },
    {
      id: "f3" as const,
      faturamentoMin: 80_000,
      faturamentoMax: 250_000,
      precoMensal: 697,
      revisaoLucro: null,
    },
    // O split em 500k é deliberado: porta de entrada e saída do preço público
    // da concorrência sales-led (decisions.md §25).
    {
      id: "f4" as const,
      faturamentoMin: 250_000,
      faturamentoMax: 500_000,
      precoMensal: 997,
      revisaoLucro: "semestral" as const,
    },
    {
      id: "f5" as const,
      faturamentoMin: 500_000,
      faturamentoMax: null,
      precoMensal: 1497,
      revisaoLucro: "trimestral" as const,
    },
  ],
};

// ---- Faixa detectada (base da cobrança) ----
// Média dos últimos 90 dias: 169.200 (derivados.lucroPeriodos["90d"]) ÷ 3
// = 56.400/mês → faixa f2. O teste do mock re-deriva essa conta.
const faturamentoMensalMedio = 169_200 / 3;
const faixaAtual = faixaParaFaturamento(plano.faixas, faturamentoMensalMedio);
const faixaDetectada = {
  faturamentoMensalMedio,
  faixaId: faixaAtual.id,
};

// ---- Assinatura (billing) ----
// Cobrança todo dia 02; mock ancorado em jun/26. Mais recente primeiro.
// UMA assinatura, com preço derivado da faixa detectada — nunca digitado.
const CARTAO = "Mastercard •••• 4821";
const METODO = { bandeira: "Mastercard", ultimos4: "4821", validade: "08/28" };

const historico = (faixaId: string, valor: number) =>
  ["06", "05", "04", "03", "02", "01"].map((mes) => ({
    id: `pg-${faixaId}-2026-${mes}`,
    data: `2026-${mes}-02`,
    valor,
    status: "pago" as const,
    metodo: CARTAO,
  }));

const assinatura = {
  faixaId: faixaAtual.id,
  plano: plano.nome,
  precoMensal: faixaAtual.precoMensal,
  // Nenhum assento além dos 3 inclusos — o card de equipe calcula ao convidar.
  acessosAdicionais: 0,
  status: "ativa" as const,
  inicio: "2026-01-02",
  proximaCobranca: "2026-07-02",
  metodoPagamento: METODO,
  historicoPagamentos: historico(faixaAtual.id, faixaAtual.precoMensal),
};

export const KOLONI_SKATESHOP: StoreData = {
  nuvemshop,
  analytics,
  ads,
  derivados,
  lucroSerie,
  waterfall,
  marketing,
  auditoria,
  coortes,
  clientes,
  custos,
  catalogo,
  abcBase,
  rankings,
  plano,
  faixaDetectada,
  assinatura,
};
